import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import {
  patchAssistantChrome,
  readAssistantChrome,
  type AssistantChromeState,
} from "../lib/assistant-chrome";

const SWIPE_MIN_PX = 24;

export function useAssistantChrome(storageKey: string) {
  const [chrome, setChrome] = useState<AssistantChromeState>(() => readAssistantChrome(storageKey));
  const drag = useRef<{ x: number; y: number; swiped: boolean } | null>(null);
  const swipedRef = useRef(false);

  useEffect(() => {
    setChrome(readAssistantChrome(storageKey));
    function onStorage(event: StorageEvent) {
      if (event.key === storageKey) setChrome(readAssistantChrome(storageKey));
    }
    function onCustom(event: Event) {
      const detail = (event as CustomEvent<{ key?: string }>).detail;
      if (detail?.key === storageKey) setChrome(readAssistantChrome(storageKey));
    }
    window.addEventListener("storage", onStorage);
    window.addEventListener("assistant-chrome", onCustom as EventListener);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("assistant-chrome", onCustom as EventListener);
    };
  }, [storageKey]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setChrome(patchAssistantChrome(storageKey, { enabled, minimized: enabled ? false : chrome.minimized }));
    },
    [chrome.minimized, storageKey],
  );

  const setMinimized = useCallback(
    (minimized: boolean) => {
      setChrome(patchAssistantChrome(storageKey, { minimized }));
    },
    [storageKey],
  );

  const trySwipe = useCallback(
    (clientX: number, clientY: number) => {
      const start = drag.current;
      if (!start || start.swiped) return false;
      const dx = clientX - start.x;
      const dy = Math.abs(clientY - start.y);
      if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > dy) {
        start.swiped = true;
        swipedRef.current = true;
        setMinimized(true);
        return true;
      }
      return false;
    },
    [setMinimized],
  );

  const consumeSwipe = useCallback(() => {
    if (!swipedRef.current) return false;
    swipedRef.current = false;
    return true;
  }, []);

  const fabPointer = {
    onPointerDown(event: ReactPointerEvent) {
      swipedRef.current = false;
      drag.current = { x: event.clientX, y: event.clientY, swiped: false };
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    },
    onPointerMove(event: ReactPointerEvent) {
      if (!drag.current) return;
      trySwipe(event.clientX, event.clientY);
    },
    onPointerUp(event: ReactPointerEvent) {
      if (drag.current) trySwipe(event.clientX, event.clientY);
      drag.current = null;
    },
    onPointerCancel() {
      drag.current = null;
    },
  };

  return { ...chrome, setEnabled, setMinimized, fabPointer, consumeSwipe };
}
